/**
 * Shared wrappers for Dexie bulk optimistic flips that participate in
 * TanStack mutation context. Both `useMarkAllSMSChatsAsRead` and
 * `useMarkAllWhatsAppChatsAsRead` follow the same pattern: capture a
 * rollback closure in `onMutate`, fire it in `onError`. Extracted here
 * so the symmetric wiring lives in one place — keeps the per-channel
 * hooks declarative and stops the duplication from tripping Sonar's
 * gate.
 */

import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type Query,
} from "@tanstack/react-query";

type Rollback = () => Promise<void>;

/**
 * Runs a Dexie optimistic flip and returns its rollback closure (or
 * `null` if the flip threw — we don't want a Dexie failure to kill the
 * whole mutation, just to skip the rollback step on error).
 */
export async function captureDexieOptimistic(
  optimistic: () => Promise<Rollback | null>,
  logPrefix: string,
): Promise<Rollback | null> {
  try {
    return await optimistic();
  } catch (err) {
    console.error(`[${logPrefix}] Dexie optimistic write failed:`, err);
    return null;
  }
}

/**
 * Invokes a captured Dexie rollback closure and explicitly handles the
 * rejection. `void rollback()` would silence the linter but leaves
 * unhandled rejections at runtime — Sonar flags that as a reliability
 * bug.
 */
export function fireDexieRollback(
  rollback: Rollback | null | undefined,
  logPrefix: string,
): void {
  rollback?.().catch((err) => {
    console.error(`[${logPrefix}] Dexie rollback failed:`, err);
  });
}

interface BulkMarkAsReadOptions<List> {
  /** The legacy bulk endpoint — server-side mark-all source of truth. */
  mutationFn: () => Promise<unknown>;
  /** Chat-list query key prefix (factory output, e.g. `smsKeys.chats()`). */
  queryKey: QueryKey;
  /**
   * Optional predicate to scope queries inside the prefix — WhatsApp
   * uses `isChatListQuery` to skip non-list queries that share the
   * prefix; SMS doesn't have that distinction and omits it.
   */
  predicate?: (query: Query) => boolean;
  /**
   * Maps an `old` chat-list page to one with `unread_count` zeroed.
   * The shape varies per channel (SMS uses `items`, WhatsApp uses
   * `chats`), so this stays per-call instead of being hard-coded.
   */
  setUnreadZero: (old: List) => List;
  /** The matching Dexie optimistic flip. */
  optimistic: () => Promise<Rollback | null>;
  /** Hook-name prefix for log lines on failure (rollback / optimistic). */
  logPrefix: string;
  /** Optional post-success side effect (WhatsApp invalidates unreadChats). */
  onSuccess?: () => void;
}

/**
 * Shared TanStack mutation shell for "mark all chats as read" flows.
 * Encapsulates the cancelQueries → getQueriesData → setQueriesData →
 * Dexie-optimistic → rollback sequence that's identical between SMS
 * and WhatsApp. Per-channel shape lives in the options object: query
 * key, list-mapper, optional predicate / onSuccess.
 */
export function useBulkMarkAsReadMutation<List>(
  opts: BulkMarkAsReadOptions<List>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: opts.mutationFn,
    onMutate: async () => {
      const filter = { queryKey: opts.queryKey, predicate: opts.predicate };
      await queryClient.cancelQueries(filter);
      const previousQueries = queryClient.getQueriesData<List>(filter);
      queryClient.setQueriesData<List>(filter, (old) =>
        old ? opts.setUnreadZero(old) : old,
      );
      const rollbackDexie = await captureDexieOptimistic(
        opts.optimistic,
        opts.logPrefix,
      );
      return { previousQueries, rollbackDexie };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      fireDexieRollback(context?.rollbackDexie, opts.logPrefix);
    },
    onSuccess: opts.onSuccess,
  });
}
